import React, { useEffect, useState } from 'react';
import { Paper, Typography, Box, Chip, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import DnsIcon from '@mui/icons-material/Dns';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

/**
 * Card de Saúde DNS — mostra X/Y nós sincronizados, com cor refletindo o pior estado.
 * Click → navega pra /dns_node.
 */
export default function DNSHealthCard() {
    const navigate = useNavigate();
    const [state, setState] = useState({ loading: true, total: 0, sync: 0, fail: 0, pending_credentials: 0 });

    useEffect(() => {
        api.get('/dashboard/oceanide/health')
            .then((res) => {
                setState({ ...res.data, loading: false });
            })
            .catch(() => setState((s) => ({ ...s, loading: false })));
    }, []);

    if (state.loading) {
        return (
            <Paper elevation={2} sx={{ m: 2, p: 2, minHeight: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={28} />
            </Paper>
        );
    }

    const allOk = state.total > 0 && state.sync === state.total && state.pending_credentials === 0;
    const allFail = state.total > 0 && state.sync === 0;
    const bgColor = allOk ? '#e8f5e9' : allFail ? '#ffebee' : state.fail > 0 ? '#fff8e1' : '#f5f5f5';
    const borderColor = allOk ? '#4caf50' : allFail ? '#e01b29' : state.fail > 0 ? '#ffb300' : '#bdbdbd';

    return (
        <Paper
            elevation={2}
            onClick={() => navigate('/dns_node')}
            sx={{
                m: 2,
                p: 2,
                minHeight: 160,
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                bgcolor: bgColor,
                borderLeft: `4px solid ${borderColor}`,
                transition: 'transform 0.1s',
                '&:hover': { transform: 'translateY(-2px)' },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <DnsIcon sx={{ color: borderColor }} />
                <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                    Saúde DNS
                </Typography>
            </Box>

            {state.total === 0 ? (
                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Nenhum nó configurado
                    </Typography>
                </Box>
            ) : (
                <>
                    <Typography variant="h3" sx={{ fontFamily: 'Montserrat', fontWeight: 400, color: borderColor, mb: 1 }}>
                        {state.sync}<span style={{ fontSize: '0.5em', color: '#888' }}>/{state.total}</span>
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        <Chip
                            icon={<CheckCircleIcon />}
                            size="small"
                            label={`${state.sync} OK`}
                            color="success"
                            variant="outlined"
                        />
                        {state.fail > 0 && (
                            <Chip
                                icon={<ErrorIcon />}
                                size="small"
                                label={`${state.fail} falha`}
                                color="error"
                                variant="outlined"
                            />
                        )}
                        {state.pending_credentials > 0 && (
                            <Chip
                                icon={<HourglassTopIcon />}
                                size="small"
                                label={`${state.pending_credentials} sem cred.`}
                                color="warning"
                                variant="outlined"
                            />
                        )}
                    </Box>
                </>
            )}
        </Paper>
    );
}
